import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { AdjacentType, sortByName } from '@iotv/datamodel-core';
import { ErrDataReducer, getObjectHash, isEdgeParams, } from '@iotv/iotv-v3-types';
import ArrowBack from '@mui/icons-material/ChevronLeft';
import ArrowFwd from '@mui/icons-material/ChevronRight';
import SearchIcon from '@mui/icons-material/Search';
import { IconButton, Stack, Typography } from '@mui/material';
import { TreeItem2Content, TreeItem2IconContainer, treeItemClasses } from '@mui/x-tree-view';
import React, { useContext, useEffect, useState } from 'react';
import { AppMainViewContext } from '../../../../ContextProviders/AppContextProvider';
import { ConsumerDao } from '../../../../data/ConsumerDAO';
import { ExtendedTreeItem } from './ExtendedTreeItemV3';
import { TypeTreeFolder } from './componentWrappers';
import { isCurriedNodeGeneratorQueryPlusExtractorParmas, } from './types';
import { useAdjacentNodeFilter } from './useAdjacentNodesFilter';
const debug = process.env.REACT_APP_DEBUG && false;
const defaultQueryLimit = 100;
/**
 * Renders leaf for context node with further child leafs rendered by decending tree according to queryPath passed down from parent level,
 */
export const ObjectTreeLeaf = ({ nodeEdgeMapper, sourceObject, contextObject, queryPaths, suppressAPIQUery, keyPrefix, onLabelClickWrapper, onTypeFolderClickWrapper, endIcon, hideThisStep, itemDerivedControls, itemDerivedTreeFn, selectable = true, setSelectedNodeIds, selectedNodeIds, orderByName = true, maxVisibleNodes, queryLimit = defaultQueryLimit, ascendentAccessor: previousAscendantAccessor, maintree_rightclick, contextMenuComponentMap, }) => {
    /** This should not be needed if nodeEdgeMapper is correctly loaded at top level NavMain and not put into state in higher level tree leafs */
    const nodeEdgeMapperItems = nodeEdgeMapper.nodeCount + nodeEdgeMapper.edgeCount;
    debug && console.log(`ObjectTreeLeafv2 for ${contextObject.sk} `, nodeEdgeMapper);
    debug && console.log('Query Paths', queryPaths);
    const [{ contextCustomer }] = useContext(AppMainViewContext);
    const selectedNodeIdsHash = getObjectHash(selectedNodeIds);
    useEffect(() => {
        debug && console.log('ObjectTreeLeaf useEffect on selectedNodeIds');
    }, [selectedNodeIdsHash]);
    /** Stores reference to queries used to populate menu nem + last evaluated keys of those queries so they may be continued if needed */
    const [queryPathTuples, setQueryPathTuples] = useState([]);
    /** runs the externally provided query and loads the results into nodeEdgeMapper*/
    const getCurriedQueryQueryPathRes = async ({ stepQuery, contextObject, nodeEdgeMapper, ExclusiveStartKey, limit }) => {
        const res = { err: null, data: null };
        debug && console.log(`getCurriedQueryQueryPathRes adjacentRes about to query with`, stepQuery, nodeEdgeMapper);
        const stepQueryRes = await stepQuery.query({ contextObject, nodeEdgeMapper, limit, ExclusiveStartKey });
        debug && console.log(`getCurriedQueryQueryPathRes adjacentRes returned ${stepQueryRes.data?.Items?.length} items`, stepQueryRes.data?.Items);
        if (stepQueryRes.data) {
            stepQueryRes.data.Items && nodeEdgeMapper.loadItems([contextObject, ...stepQueryRes.data.Items]);
            res.data = {
                LastEvaluatedKey: stepQueryRes.data.LastEvaluatedKey,
            };
        }
        debug && console.log(`getCurriedQueryQueryPathRes returned ${stepQueryRes.data?.Items?.length} items`);
        debug && console.log(stepQueryRes.data?.Items);
        return res;
    };
    /**
     * get the core tuple for a single queryPath, mutates nodeEdgeMapper
     */
    const getEdgeParamsQueryPathRes = async ({ nodeEdgeMapper, contextObject, stepQuery, ExclusiveStartKey = undefined, limit = queryLimit, }) => {
        const res = { err: null, data: null };
        if (isEdgeParams(stepQuery)) {
            const request = {
                ...stepQuery,
                adjacencyType: stepQuery.adjacencyType ?? AdjacentType.CHILD,
                scopeDefinitingContextObject: contextObject,
                ExclusiveStartKey,
                includeEdges: true,
                limit,
            };
            const adjacentRes = await ConsumerDao.getAdjacent(request);
            if (adjacentRes.data) {
                adjacentRes.data.Items && nodeEdgeMapper.loadItems([contextObject, ...adjacentRes.data.Items]);
                res.data = {
                    LastEvaluatedKey: adjacentRes.data.LastEvaluatedKey,
                };
            }
            debug && console.log(`getQueryPathRes returned ${adjacentRes.data?.Items?.length} items`);
            debug && console.log(adjacentRes.data?.Items);
        }
        return res;
    };
    /**
     * Arbitrates query handling, supprted types: Simple EdgeParams,
     * @returns - An object with the last evaluated key of the query
     * INFO: The actual node edge mapper population occurs in the query fns. The return is used for record keeping
     */
    const getQueryPathRes = async ({ nodeEdgeMapper, contextObject, stepQuery, ExclusiveStartKey = undefined, limit = queryLimit, }) => {
        let res = { err: null, data: null };
        debug && console.log('getQueryPathRes: Query Path Res Context Object', contextObject);
        try {
            if (isEdgeParams(stepQuery)) {
                res = await getEdgeParamsQueryPathRes({ nodeEdgeMapper, contextObject, stepQuery, ExclusiveStartKey, limit });
            }
            else if (isCurriedNodeGeneratorQueryPlusExtractorParmas(stepQuery)) {
                /** Potentially generates many of the same query if executing an api indirect query - consider adding required context in start up (see Region queries) */
                res = await getCurriedQueryQueryPathRes({ nodeEdgeMapper, contextObject, stepQuery, ExclusiveStartKey, limit });
            }
        }
        catch (e) {
            console.log(`ERR: ObjectTreeLeaf caught err in getQueryPathRes`, { message: e.message, stepQuery, err: e });
            res.err = e;
        }
        return res;
    };
    /**
     * populates the core tuples for each queryPath configured for this level
     * Run only when the ObjectTreeLeaf is rendered for the first time
     */
    const populateNodeEdgeMapper = async (queryPaths) => {
        const queryPromises = queryPaths.map(async (queryPath) => {
            const { stepQuery, suppressThisApiQuery } = queryPath;
            if (suppressThisApiQuery !== true) {
                debug && console.log(`warn: querying!`);
                return getQueryPathRes({ nodeEdgeMapper, contextObject, stepQuery }); // Mutates the nem
            }
            else {
                debug && console.log(`warn: step has suppressThisApiQuery true, suppressing api query!`);
                return { err: null, data: { LastEvaluatedKey: undefined } };
            }
        });
        const combinedRes = ErrDataReducer(await Promise.all(queryPromises));
        if (combinedRes.data) {
            debug && console.log('queryPathRes', combinedRes.data);
            const queryPathMapperTuples = combinedRes.data.map(({ LastEvaluatedKey }, i) => [queryPaths[i], LastEvaluatedKey]);
            debug && console.log('PathMapperTuples', queryPathMapperTuples);
            setQueryPathTuples(queryPathMapperTuples);
        }
        if (combinedRes.err) {
            console.log('CombinedRes handleErr', combinedRes.err.message);
        }
    };
    /** API query run once on first render */
    useEffect(() => {
        debug && console.log('useEffect on first render');
        const queryPathMapperTuples = queryPaths.map((queryPath, i) => [queryPath, undefined]);
        setQueryPathTuples(queryPathMapperTuples);
        if (suppressAPIQUery !== true) {
            try {
                populateNodeEdgeMapper(queryPaths);
            }
            catch (e) {
                console.log(`ERR: ObjectTreeLeaf caught `, e);
            }
        }
        else {
            debug && console.log(`Suppressed API Query`);
        }
    }, []);
    useEffect(() => {
        debug && console.log('ObjectTreeLeafv2', 'useEffect on item count');
    }, [nodeEdgeMapperItems]);
    /** Used to restrict how many items can be viewed on the menu at once */
    const [[fromIdx, toIdx], setIndexRange] = useState([0, maxVisibleNodes ?? Infinity]);
    debug && console.log('Node Filter Key Prefix', keyPrefix);
    /** ideally filter label should the type of objects returned from this step */
    const { filterComponent, adjacentNodeFilter } = useAdjacentNodeFilter({ keyPrefix });
    /**
     * requery and add to nodeEdgeMapper when user clicks more...
     */
    const handleNextResults = async (e, queryPathIndex) => {
        e.stopPropagation();
        const [queryPath, LastEvaluatedKey] = queryPathTuples[queryPathIndex];
        const res = await getQueryPathRes({ nodeEdgeMapper, contextObject, stepQuery: queryPath.stepQuery, ExclusiveStartKey: LastEvaluatedKey });
        if (res.data) {
            const queryPathMapperTuple = [queryPath, res.data.LastEvaluatedKey];
            const queryPathMapperTypleClone = [...queryPathTuples];
            queryPathMapperTypleClone.splice(queryPathIndex, 1, queryPathMapperTuple);
            setQueryPathTuples(queryPathMapperTypleClone);
        }
    };
    const allowAll = (item) => item;
    /**
     * Creates the child leafs from each of the core tuples
     * Doesn't populate the nem, but does get nodes to turn into tree nodes by running the query from the tuple on the nem.
     * The query path from the tuple *is* the one that populates the nem, but we don't make tree nodes on population because this logic is very busy, so it's just here
     */
    const treeNodesByQueryPathRes = queryPathTuples.map(([queryPath, LastEvaluatedKey], i) => {
        debug && console.log('Filtered Nodes treeNodesByQueryPathRes', queryPath);
        const { stepQuery, nodeFilter } = queryPath;
        const edgeFilter = isEdgeParams(stepQuery) ? stepQuery : stepQuery.edgeFilter;
        /** tree leaves regernated after each render */
        const adjacentNodesAndEdges = nodeEdgeMapper.getAdjacentNodesAndEdges(contextObject, edgeFilter);
        const allNodes = adjacentNodesAndEdges.nodes;
        if (orderByName)
            allNodes.sort(sortByName);
        const filteredNodes = allNodes
            .filter(nodeFilter ?? allowAll)
            .filter(adjacentNodeFilter)
            .slice(fromIdx, toIdx);
        debug && console.log('Filtered Nodes', filteredNodes);
        const queryPathLeafs = filteredNodes.map((node) => {
            debug && console.log(`${node.name} has sourceObject ${contextObject?.name}`);
            const nextQueryPaths = typeof queryPath.nextSteps === 'function' ? queryPath.nextSteps({ treeNode: node }) : (queryPath.nextSteps ?? []);
            return (_jsx(ObjectTreeLeaf, { keyPrefix: `${keyPrefix}_${node.sk}`,
                nodeEdgeMapper,
                sourceObject: contextObject,
                contextObject: node,
                queryPaths: nextQueryPaths,
                suppressAPIQUery,
                onLabelClickWrapper: queryPath.thisStepLabelWrapper ?? onLabelClickWrapper,
                endIcon: queryPath.endIcon,
                hideThisStep: queryPath.hideThisStep,
                itemDerivedControls: queryPath.itemDerivedControls,
                itemDerivedTreeFn: queryPath.itemDerivedTreeFn,
                onTypeFolderClickWrapper: onTypeFolderClickWrapper,
                selectable,
                setSelectedNodeIds,
                selectedNodeIds,
                maxVisibleNodes,
                orderByName,
                ascendentAccessor: { ascendentAccessor: previousAscendantAccessor, contextObject },
                maintree_rightclick,
                contextMenuComponentMap }, `${keyPrefix}_${node.sk}`));
        });
        /**
         * if there are more results add a node with a link to get the next page
         */
        if (LastEvaluatedKey) {
            queryPathLeafs.push(_jsx(ExtendedTreeItem, { keyIn: `${keyPrefix}_lastEvaluated_${i}`,
                x: _jsx(_Fragment, {}),
                label: (_jsx("span", { style: { opacity: 0.65 }, onClick: (e) => handleNextResults(e, i), children: _jsx("i", { children: "More..." }) })),
                contextObject,
                contextCustomer }));
        }
        /** Render node slice controls if tree has limit on number of visible nodes */
        const from = fromIdx > 0;
        const to = toIdx < allNodes.length;
        if (maxVisibleNodes && allNodes.length > maxVisibleNodes) {
            queryPathLeafs.unshift(
            /** Stop the onKeyUp and onKeyDown events propagating up to the parent TreeItem / TreeView so that the search text box can capture focus correctly */
            _jsx("div", { className: treeItemClasses.root, onKeyUp: (event) => event.stopPropagation(), onKeyDown: (event) => event.stopPropagation(), children: _jsxs(TreeItem2Content, { status: {
                        disabled: false,
                        editable: false,
                        editing: false,
                        expandable: false,
                        expanded: false,
                        focused: false,
                        selected: false,
                    }, children: [_jsx(TreeItem2IconContainer, { children: _jsx(SearchIcon, {}) }), filterComponent] }) }, `${LastEvaluatedKey}`)); // TODO: this is wonky and doesn't work right with focus and the tree, etc.
            queryPathLeafs.push(_jsxs(Stack, { direction: 'row', sx: {
                    justifyContent: 'space-between',
                    paddingInline: 1,
                }, children: [_jsx(IconButton, { disabled: !from, sx: { marginBlock: -1 }, onClick: () => setIndexRange([fromIdx - maxVisibleNodes, toIdx - maxVisibleNodes]), children: _jsx(ArrowBack, { color: "primary", sx: { opacity: from ? 1 : 0 } }, `${keyPrefix}_back`) }), _jsx(Typography, { children: `${fromIdx}–${toIdx} of ${allNodes.length}` }, `${keyPrefix}_idx`), _jsx(IconButton, { disabled: !to, sx: { marginBlock: -1 }, onClick: () => setIndexRange([fromIdx + maxVisibleNodes, toIdx + maxVisibleNodes]), children: _jsx(ArrowFwd, { color: "primary", sx: { opacity: to ? 1 : 0 } }, `${keyPrefix}_fwd`) })] }, `${keyPrefix}_maxVisibleNodes`));
        }
        /**
         * If config requires type folder wrap the tree items with a TypeTreeFolder
         */
        if (queryPath.renderTypeFolder) {
            return (_jsx(TypeTreeFolder, { queryPath,
                sourceObject: contextObject,
                children: queryPathLeafs,
                LastEvaluatedKey,
                keyPrefix: `${keyPrefix}_${i}`,
                onTypeFolderClickWrapper: onTypeFolderClickWrapper ?? queryPath.thisStepTypeFolderClickWrapper,
                contextMenuComponentMap }, `${keyPrefix}_${contextObject.sk}`));
        }
        else {
            return queryPathLeafs;
        }
    });
    /** Create optional items from the itemDerivedTreeFn, if present **/
    const itemDerivedTreeLeafs = [];
    if (itemDerivedTreeFn) {
        debug && console.log(`ObjectTreeLeafv2 creating unusual items for ${contextObject.sk} has itemDerivedTreeFn`, !!itemDerivedTreeFn);
        itemDerivedTreeLeafs.unshift(...itemDerivedTreeFn(contextObject, nodeEdgeMapper));
    }
    else {
        debug && console.log(`ObjectTreeLeafv2 not creating unusual items, but queryPath has itemdDevicedFn `, { itemDerivedTreeFn });
    }
    /** Render the tree item for this contextObject */
    const renderableTreeItems = [...treeNodesByQueryPathRes, ...itemDerivedTreeLeafs];
    return !hideThisStep ? (_jsx(ExtendedTreeItem, { keyIn: `${keyPrefix}_${contextObject.sk}`, contextMenuComponentMap: contextMenuComponentMap, label: onLabelClickWrapper?.({
            ascendentAccessor: previousAscendantAccessor,
            selectable,
            setSelectedNodeIds,
            selectedNodeIds,
            vob: contextObject,
            sourceObject,
            element: _jsx(Typography, { flexGrow: 1, children: `${contextObject.name}` }),
        }) ?? _jsx(Typography, { flexGrow: 1, children: `${contextObject.name}` }), x: itemDerivedControls ? _jsx(_Fragment, { children: itemDerivedControls(contextObject, selectedNodeIds) }) : _jsx(_Fragment, {}), contextObject: contextObject, maintree_rightclick: maintree_rightclick, slots: {
            endIcon: typeof endIcon === 'function'
                ? (endIcon(contextObject) ?? undefined)
                : endIcon,
        }, children: renderableTreeItems })) : (_jsx(_Fragment, { children: renderableTreeItems }));
};
